// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pure rules of the "From Konvo" list (docs/konvo-assistant/ready-list.md): reading the
// server's answer item by item, a pop-up that folded into the list before the server knows, what
// was acted on here (hidden at once, or moved to Done today) and the one-line peek beside the
// icon. No stores here, so the rules are unit tested (ReadyListModel.test.ts).

import {HttpError} from '@app/features/platform/types/EndpointError';
import {
	type CornerSuggestion,
	type ReadyItem,
	ReadyItemSchema,
} from '@fluxer/schema/src/domains/konvo/AssistantEngagement';

/** Send reply was refused because the meeting time the reply offers has started (409 time_passed). */
export function isTimePassed(error: unknown): boolean {
	if (!(error instanceof HttpError) || error.status !== 409) return false;
	const body = error.body as {error?: unknown} | null | undefined;
	return body?.error === 'time_passed';
}

/** What the list endpoint answered, read item by item: one bad entry does not empty the list. */
export function parseReadyList(body: unknown): {items: Array<ReadyItem>; done: Array<ReadyItem>} | null {
	if (!body || typeof body !== 'object') return null;
	const read = (raw: unknown): Array<ReadyItem> =>
		(Array.isArray(raw) ? raw : []).flatMap((entry) => {
			const parsed = ReadyItemSchema.safeParse(entry);
			return parsed.success ? [parsed.data] : [];
		});
	const record = body as {items?: unknown; done?: unknown};
	if (!Array.isArray(record.items)) return null;
	return {items: read(record.items), done: read(record.done)};
}

/** The list entry for a corner suggestion (`c:<id>`), as the server builds it. */
export function readyItemId(suggestionId: string): string {
	return `c:${suggestionId}`;
}

/** A pop-up with a ready card as a list entry (folded before the next load, or acted on from the pop-up). */
export function itemForSuggestion(suggestion: CornerSuggestion, at: string): ReadyItem | null {
	if (!suggestion.ready) return null;
	return {
		id: readyItemId(suggestion.id),
		source: 'corner',
		kind: suggestion.kind,
		at,
		state: 'ready',
		card: suggestion.ready,
		suggestion,
	};
}

/** Newest first; a folded pop-up the server has not listed yet goes on top. */
export function withFolded(items: ReadonlyArray<ReadyItem>, folded: ReadyItem): Array<ReadyItem> {
	return [folded, ...items.filter((item) => item.id !== folded.id)];
}

export interface LocalChange {
	/** When it was acted on here. */
	at: number;
	/** Done here (sent, kept): shown under Done today with this note until the server lists it. */
	doneNote: string | null;
	item: ReadyItem;
}

/** How long something acted on here stays hidden while the server catches up. */
export const LOCAL_CHANGE_MS = 60_000;

/**
 * What the list shows: the server's ready items less what was acted on here a moment ago, and
 * Done today with what was done here first (until the server lists it as done too).
 */
export function applyLocalChanges(
	items: ReadonlyArray<ReadyItem>,
	done: ReadonlyArray<ReadyItem>,
	changes: ReadonlyMap<string, LocalChange>,
	now: number,
): {items: Array<ReadyItem>; done: Array<ReadyItem>} {
	const live = (id: string) => {
		const change = changes.get(id);
		return change != null && now - change.at < LOCAL_CHANGE_MS;
	};
	const doneIds = new Set(done.map((item) => item.id));
	const localDone: Array<ReadyItem> = [];
	for (const [id, change] of changes) {
		if (!live(id) || change.doneNote == null || doneIds.has(id)) continue;
		localDone.push({
			...change.item,
			state: 'done',
			at: new Date(change.at).toISOString(),
			done_note: change.doneNote,
		});
	}
	localDone.sort((a, b) => b.at.localeCompare(a.at));
	return {
		items: items.filter((item) => !live(item.id)),
		done: [...localDone, ...done].slice(0, 10),
	};
}

/** The first line of a ready card that is not the headline, for a collapsed entry. */
export function entryDetail(item: ReadyItem): string | null {
	const line = item.card.lines[0];
	if (line) return line.detail ? `${line.title} · ${line.detail}` : line.title;
	return item.card.note ?? item.card.reply?.body ?? null;
}

/** The peek's headline: the folded one if it is still listed, else the newest. */
export function peekHeadline(items: ReadonlyArray<ReadyItem>, foldedId: string | null): string | null {
	const folded = foldedId ? items.find((item) => item.id === foldedId) : undefined;
	return (folded ?? items[0])?.card.headline ?? null;
}
